﻿import { useEffect, useState } from 'react';
import { StrikeThroughHeader } from 'Components/Headers/strikethrough-header/strikethrough-header';
import { useQuery } from '@tanstack/react-query';
import { DropDownForDateRange, GetAppDownloadList } from 'api/appdownload.js';
import { useUser } from 'context/UserContext';
import {
  Box,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Paper,
  TextField,
  Stack,
} from '@mui/material';
import LoadingIndicator from '../../../../components/Global/loading-indicator/loading-indicator';
import ProducerDropdown from './components/ProducerDropdown';
import { styled } from '@mui/material/styles';
import { Snackbar } from '../../../../components/Global/Snackbar/Snackbar';
import { AppList } from './components/AppList';

const StyledStack = styled(Stack)(({ theme }) => ({
  display: 'flex',
  gap: '1rem',
  padding: '1rem',
  height: 'fit-content',
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
    gap: '.5rem',
  },
}));

export default function MyIssuedApps() {
  const [policyID, setPolicyID] = useState('');
  const [appList, setAppList] = useState([]);
  const user = useUser();
  const [producerID, setProducerID] = useState('');
  const [selectedDateRange, setSelectedDateRange] = useState('');
  const [snackbarDetails, setSnackbarDetails] = useState({
    open: false,
    status: '',
    message: '',
  });

  var maxPolicyDownloadCount = 30;

  const { isPending: isDropdownSelectLoading, data: dateRangeSelectList } = useQuery({
    queryKey: ['dropDownRangeSelect'],
    queryFn: () => DropDownForDateRange()(),
  });

  useEffect(() => {
    const list = Array.isArray(dateRangeSelectList)
      ? dateRangeSelectList
      : dateRangeSelectList?.itemList;
    if (list?.[0]?.value) {
      setSelectedDateRange(list[0].value);
    }
  }, [dateRangeSelectList]);

  const appDownloadListRows = useQuery({
    queryKey: ['getAppDownloadList', selectedDateRange, policyID, producerID],
    queryFn: () =>
      GetAppDownloadList({
        Division: user.producerID === 'HO' ? 'HO' : user.division,
        ProducerID: producerID !== '' ? producerID : user.producerID.split(',')[0],
        PolicyID: policyID,
        DateRangeTo: selectedDateRange,
      })(),
    enabled: !!selectedDateRange,
  });

  useEffect(() => {
    if (appDownloadListRows.data) {
      const data = appDownloadListRows.data;

      if (data === 'An error has occurred') {
        setAppList([]);
        setSnackbarDetails({
          open: true,
          status: 'error',
          message: 'An error has occurred.',
        });
        return;
      }

      if (data === 'Invalid ProducerID') {
        setAppList([]);
        setSnackbarDetails({
          open: true,
          status: 'error',
          message: 'Invalid ProducerID.',
        });
        return;
      }

      if (data === 'No Access to PolicyID') {
        setAppList([]);
        setSnackbarDetails({
          open: true,
          status: 'error',
          message: 'No access to policy ID.',
        });
        return;
      }

      setAppList(data.applicationList);
    }
  }, [appDownloadListRows.data]);

  return (
    <>
      <Snackbar
        open={snackbarDetails.open}
        duration={3000}
        message={snackbarDetails.message}
        status={snackbarDetails.status}
        onClose={() =>
          setSnackbarDetails({
            ...snackbarDetails,
            open: false,
          })
        }
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />

      <StrikeThroughHeader>My Issued Applications</StrikeThroughHeader>
      <Paper>
        {isDropdownSelectLoading ? (
          <Box sx={{ padding: '2rem 0' }}>
            <LoadingIndicator />
          </Box>
        ) : (
          <>
            <StyledStack>
              <ProducerDropdown
                setProducerID={(id) => {
                  setProducerID(id);
                }}
              />
              <TextField
                placeholder=""
                value={policyID}
                label="Policy ID (optional)"
                variant="outlined"
                onChange={(e) => {
                  setPolicyID(e.target.value);
                }}
              />
              <FormControl>
                <InputLabel id="date-range-label">Date Range</InputLabel>
                <Select
                  labelId="date-range-label"
                  id="date-range"
                  value={selectedDateRange}
                  label="Date Range"
                  onChange={(e) => {
                    setSelectedDateRange(e.target.value);
                  }}
                >
                  {(Array.isArray(dateRangeSelectList)
                    ? dateRangeSelectList
                    : dateRangeSelectList?.itemList
                  )?.map((d, index) => (
                    <MenuItem id={index} key={d.value} value={d.value}>
                      {d.text}
                      {}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </StyledStack>
            <AppList
              producerID={producerID}
              user={user}
              isLoading={appDownloadListRows.isLoading}
              maxPolicyDownloadCount={maxPolicyDownloadCount}
              appList={appList}
              setSnackbarDetails={setSnackbarDetails}
            />
          </>
        )}
      </Paper>
    </>
  );
}
